import { EcmRole } from "../features/navigation/Subscription";

// Type definitions
export interface URLToPageNameMapType {
    [key: string]: string;
}

export interface StatusDescriptionsType {
    [key: string]: string;
}

export interface HttpStatusCodeType {
    Success: number;
    NotFound: number;
    BadRequest: number;
    NotImplemented: number;
    InternalServerError: number;
    Created: number;
}

export interface TemplateOption {
    value: string;
    label: string;
    downloadLink: string;
    description: string;
}

export interface CustomFieldType {
    label: string;
    value: string;
    selected: boolean;
}

export interface RoleOption {
    label: string;
    value: string;
}

// Field tag constants for customer forms
export const customerCodeTag = "customerCode";
export const customerNameTag = "customerName";
export const addressLine1Tag = "addressLine1";
export const addressLine2Tag = "addressLine2";
export const cityTag = "city";
export const stateTag = "state";
export const zipCodeTag = "zipCode";
export const countryIdTag = "countryId";
export const countryNameTag = "countryName";
export const contactNameTag = "contactName";
export const phoneNumberTag = "phoneNumber";
export const emailAddressTag = "emailAddress";

export const URLToPageNameMap: URLToPageNameMapType = {
    "/search/customers": "search-customers",
    "/search/certificates": "search-certificates",
    "/search/jobs": "search-jobs",
    "/add-customer": "add-customer",
    "/add-job": "add-job",
    "/jobs/#": "job-details",
    "/customer/#": "customer-details",
    "/certificate/#": "certificate-details",
    "/import-customers": "import-customers",
    "/search/pending": "incoming-certificates",
    "/review": "review-certificates",
    "/review/#": "review-certificate",
    "/requests": "requested-certificates",
    "/requests/bulkRequests": "requested-certificates-bulk",
    "/requests/singleRequests": "requested-certificates-single",
    "/campaigns/#": "view-certificate-requests",
    "/requests/create": "certificates-request-1",
    "/requests/create/2": "certificates-request-2",
    "/requests/create/3": "certificates-request-3",
    "/requests/request-dialog": "certificate-request",
    "/resources": "resources",
    "/resources/tax-answers": "resources-tax-answers",
    "/resources/tax-laws": "resources-tax-laws",
    "/resources/exempt-entities": "resources-exempt-entities",
    "/resources/certificate-forms": "resources-certificate-forms",
    "/reports/saved-searches": "saved-searches",
    "/reports/admin-reports": "admin-reports",
    "/settings/general": "settings-general",
    "/settings/certificateRequests": "settings-certificate-requests",
    "/settings/certificateFormsInUse": "settings-certificate-forms-in-use",
    "/settings/automations": "settings-automations",
};

export const StatusDescriptions: StatusDescriptionsType = {
    E: "Error",
    I: "In process",
    C: "Closed",
    D: "Deleted",
    G: "Generated",
    Z: "Sent",
    S: "Sending",
};

export const invalidReasonDialogID = "invalid-reason-dialog";

export const HttpStatusCode: HttpStatusCodeType = {
    Success: 200,
    NotFound: 404,
    BadRequest: 400,
    NotImplemented: 501,
    InternalServerError: 500,
    Created: 201,
};

export const Messages = {
    saved_Success: "The current search has been successfully saved.",
    saved_failure: "The current search cannot be saved.",
};

/** Shown when datepicker input is invalid (e.g. onS-error). */
export const INVALID_DATE_MESSAGE = "Enter a valid date";

export const coverLetterOptions = [
    { label: "Standard Vendor Template", value: "Default", selected: true },
];

export const ClaimedByList = [
    {
        label: "Me",
        value: "1",
    },
    {
        label: "Someone else",
        value: "2",
    },
    {
        label: "Unassigned",
        value: "3",
    },
];

export const EscalatedList = [
    {
        label: "Show only escalated certificates",
        value: "1",
    },
    {
        label: "Exclude escalated certificates",
        value: "2",
    },
];

export const TaxNumberTypeList = [
    {
        label: "Tax ID Number",
        value: ["Tax ID Number", "Tax ID number", "TIN", "Exemption Number/Taxpayer ID"],
        selected: false,
    },
    {
        label: "FEIN",
        value: ["FEIN"],
        selected: false,
    },
    {
        label: "Driver’s license",
        value: ["Driver's License", "Driver's License Number", "DLN", "Driver’s license"],
        selected: false,
    },
    {
        label: "Foreign diplomat number",
        value: ["Foreign diplomat number", "Foreign Diplomat Number", "FDN"],
        selected: false,
    },
];

export const TemplateTypes = {
    BASIC_CUSTOMERS_TEMPLATE: "Basic customers template",
    BILL_TO_SHIP_TO_CUSTOMERS_TEMPLATE: "Bill-to and ship-to customers template",
    CUSTOMER_NUMBER_CHANGE_TEMPLATE: "Customer number change template",
};

export const generateTemplateOptions: TemplateOption[] = [
    {
        value: TemplateTypes.BASIC_CUSTOMERS_TEMPLATE,
        label: TemplateTypes.BASIC_CUSTOMERS_TEMPLATE,
        downloadLink: "customer_upload_template_basic.xlsx",
        description:
            "Import multiple customers based on their billing information and where they're exempt.",
    },
    {
        value: TemplateTypes.BILL_TO_SHIP_TO_CUSTOMERS_TEMPLATE,
        label: TemplateTypes.BILL_TO_SHIP_TO_CUSTOMERS_TEMPLATE,
        downloadLink: "customer_upload_template.xlsx",
        description:
            "Import multiple customers that are exempt in more than one region or have separate billing and shipping information.",
    },
    {
        value: TemplateTypes.CUSTOMER_NUMBER_CHANGE_TEMPLATE,
        label: TemplateTypes.CUSTOMER_NUMBER_CHANGE_TEMPLATE,
        downloadLink: "bulk_customer_number_upload_template.xlsx",
        description: "Update existing customers' identification numbers to a new value.",
    },
];

export const AllowedTaxNumberTypeList = [
    "Tax ID Number",
    "Tax ID number",
    "Exemption Number/Taxpayer ID",
    "FEIN",
    "TIN",
    "Driver's License",
    "Driver's License Number",
    "DLN",
    "Driver’s license",
    "Foreign diplomat number",
    "Foreign Diplomat Number",
    "FDN",
];

export const DefaultCoverLetters = {
    standardRequest: "STANDARD_REQUEST",
    ecmDefault: "Default Cover Letter",
    vemDefault: "Standard Vendor Template",
};

export const roleKeyValue = {
    [EcmRole.CEMAdmin]: "Administrator",
    [EcmRole.CEMManager]: "Manager",
    [EcmRole.CEMCertificateReviewer]: "Certificate Reviewer",
    [EcmRole.CEMViewer]: "Viewer",
    [EcmRole.CEMNoAccess]: "No Access",
    [EcmRole.RETManager]: "Retail Manager",
    [EcmRole.RETAssociate]: "Retail Associate",
    [EcmRole.RETViewer]: "Retail Viewer",
    [EcmRole.CEMPreValidator]: "Pre Validator",
};

export const roleOptionList: RoleOption[] = Object.entries(roleKeyValue).map(([value, label]) => ({
    label,
    value,
}));

export const documentTypeNames = {
    salesAndUseTax: "Sales and Use Tax",
    federalWithholding: "Federal Withholding",
};

export const countryIds = {
    US: "1",
};

export const BackgroundJobState = {
    Queued: 1,
    Processing: 2,
    Completed: 3,
};
export const vendorPackageDummy = "vendorPackageDummy";

export const customFieldTypes: CustomFieldType[] = [
    { label: "Text", value: "text", selected: false },
    { label: "Select", value: "select", selected: false },
    { label: "Multi Select", value: "multi-select", selected: false },
    { label: "Logical", value: "boolean", selected: false },
];

export const allSettings = {
    show_no_cert: { label: "Show customers without certificates", type: "client" },
    show_historical: { label: "Show historical certificates", type: "retailSettingUpdates" },
    show_no_pending: {
        label: "Include certificates that haven't been reviewed yet.",
        type: "client",
    },
    fill_only: { label: "Disable certificate image upload", type: "retailSettingUpdates" },
    upload_form_unavailable: {
        label: "Allow certificate image upload only when a fillable form is not available",
        type: "retailSettingUpdates",
    },
    append_barcode: {
        label: "Append a barcode to new certificates",
        type: "retailSettingUpdates",
    },
    certid_footer: {
        label: "Append the ECM certificate ID to new certificates",
        type: "retailSettingUpdates",
    },
    submit_to_stack: {
        label: "Require review of new certificates(Do not automatically mark as valid)",
        type: "retailSettingUpdates",
    },
    preview: {
        label: "Allow print, preview and download of certificates",
        type: "retailSettingUpdates",
    },
};
export const avataxAPUrls = ["/search/vendors", "/add-vendor", "/vendor/"];

export const UnitedStates = "United States";

export const Canada = "Canada";

export const documentTypeOutgoing = false;

export const SalesAndUseTax = "Sales and Use Tax";

export const pendingCertificateColumnPreference = "pending-certificate-columns";

export const PendingStatuses = ["PENDING", "PENDING-SINGLE", "PENDING-MULTI"];

export const CertificateStatuses = {
    Complete: "COMPLETE",
    PENDING_FUTURE: "PENDING-FUTURE",
    Pending: "PENDING",
    PendingFuture: "Pending-future",
};

export const CERTFICATE_SAVE_STATUS = {
    PENDING: "Pending",
    INVALID: "Invalid",
    VALID: "Valid",
};

// Default tax number type for certificates when value is missing
export const DEFAULT_TAX_NUMBER_TYPE = "Tax ID Number";

export const AlertType = {
    Warning: "warning",
    Info: "info",
    Success: "success",
    Error: "error",
};

export const TEMPLATE_NOT_SELECTED_ERROR =
    "The file doesn't match a selected template. Select the template and upload the corresponding file to continue.";

export const TaxIdAlertReason = {
    RegexTest: "RegexTest",
    Idvalidation: "IdValidation",
    ValidationNotSupported: "OnlineValidationNotSupported",
};

export const RequestStatuses = {
    EXPIRING: "Expiring",
    EXPIRING_SOON: "Expiring soon",
    EXPIRED: "Expired",
    VALID: "Valid",
    INVALID: "Invalid",
    MISSING: "Missing",
    PENDING: "Pending",
    PAUSED: "Paused",
};

export const completeRequestStatuses = [
    RequestStatuses.VALID,
    RequestStatuses.INVALID,
    RequestStatuses.EXPIRED,
    RequestStatuses.PAUSED,
];

export enum Environments {
    LOCAL = "local",
    DEV = "dev",
    QA = "qa",
    SBX = "sbx",
    PRD = "prd",
}

export const lowerEnvironments = [Environments.LOCAL, Environments.DEV, Environments.QA];
export const upperEnvironments = [Environments.SBX, Environments.PRD];

/**
 * Delay in milliseconds for type ahead search
 * @type {{
 *   CustomerQuickSearch: number,
 *   CustomerSearchGrid: number,
 *   PendingCertificateSearchGrid: number,
 *   VendorSearchGrid: number,
 *   VendorCertificateSearchGrid: number,
 *   CertificateSearchGrid: number
 * }}
 */
export const typeAheadDelay = {
    CustomerSearchGrid: 600,
    CustomerQuickSearch: 600,
    VendorSearchGrid: 600,
    VendorCertificateSearchGrid: 600,
    VendorPackageSearchGrid: 600,
    CertificateSearchGrid: 600,
    PendingCertificateSearchGrid: 600,
};

export const StatementOfTaxability = "Statement Of Taxability";
export const NonDeliverable = "Non-Deliverable";
export const Taxable = "taxable";
export const countryInitials = {
    US: "US",
    CA: "CA",
};
export const CRUDOperation = {
    ReadAll: "ReadAll",
    Read: "Read",
    Create: "Create",
    Update: "Update",
    Delete: "Delete",
};
export const UnhandledError = "UnhandledError";
export const ACTIVITY_STORAGE_KEY = "user_last_activity";
export const PREVIOUS_ACCOUNT_ID_COOKIE_NAME = "__previousAccountId__";
export const UnkownError = "Something went wrong";

export const LogTypes = [
    { label: "All Log Types", value: "" },
    { label: "Account Settings", value: "Account Settings" },
    { label: "Customer", value: "Customer" },
    { label: "Certificate", value: "Certificate" },
    { label: "Certificate Attribute", value: "Certificate Attribute" },
    { label: "Company Division", value: "Company Division" },
    { label: "Company Entity", value: "Company Entity" },
    { label: "Customer Attribute", value: "Customer Attribute" },
    { label: "Exempt Reason", value: "Exempt Reason" },
    { label: "Exposure Zone", value: "Exposure Zone" },
    { label: "External API Settings", value: "External API Settings" },
    { label: "Invalid Reason", value: "Invalid Reason" },
    { label: "User Account", value: "User Account" },
    { label: "User Role", value: "User Role" },
    { label: "API Call", value: "API Call" },
    { label: "Exemption Matrix", value: "Exemption Matrix" },
    { label: "Data Entry", value: "Data Entry" },
    { label: "Default Exemption Matrix", value: "Default Exemption Matrix" },
    { label: "ECM Settings - General", value: "ECM Settings - General" },
    { label: "ECM Settings - Certificate", value: "ECM Settings - Certificate" },
    { label: "ECM Settings - Certificate Requests", value: "ECM Settings - Certificate Requests" },
    { label: "ECM Settings - CertExpress", value: "ECM Settings - CertExpress" },
    { label: "ECM Settings - Internal", value: "ECM Settings - Internal" },
];

export const ExportTypes = {
    Customer: "Customer",
    Vendor: "Vendor",
    Certificate: "Certificate",
    VendorCertificate: "Vendor-Certificate",
    PendingCertificate: "Pending-Certificate",
};
export const BUSINESS_TYPE_OTHER = "Other";

export const rowsPerPageDefault = 20;

export const daysOfTheWeek = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
];

export const FILE_MIME_TYPES = {
    XLSX: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
};

export const CertificateSearch = "CertificateSearch";
export const CustomerSearch = "CustomerSearch";
export const resyncApiCallToastMessage =
    "Your sync request has been added to the queue. It may take a few minutes to process.";

export const DeliveryMethodType = {
    Email: "Email",
    Download: "Download",
};

export const DeliveryMethod = {
    EMAIL: "email",
    PDF: "pdf",
    ZIP: "zip",
    STACK: "stack",
};
export const SpecifyRegionReasonLabel = "Specify a region and reason for the exemption certificate";

export const POLLER_RESPONSE_STATUSES = {
    COMPLETED: "Completed",
    SUCCESS: "Success",
    ERRORS_FOUND: "Errors found",
    ERROR: "Error",
};

export const CampaignRoundTypes = {
    Round_1: "Initial request",
    Round_2: "1st follow up request",
    Round_3: "2nd follow up request",
    Round_4: "3rd follow up request",
    Round_5: "4th follow up request",
    Round_6: "5th follow up request",
    Round_7: "6th follow up request",
    Round_8: "7th follow up request",
    Round_9: "8th follow up request",
    Round_10: "9th follow up request",
};

// Filter for bulk requests: exclude deleted and closed campaigns (OData syntax)
export const BULK_REQUESTS_FILTER = "status ne 'D' and status ne 'C'";
// Filter for deleted requests: only deleted campaigns (OData syntax)
export const DELETED_REQUESTS_FILTER = "status eq 'D'";

// Request type identifiers used across tabs, routes, and NoData component
export const REQUEST_TYPES = {
    BULK_REQUESTS: "bulkRequests",
    SINGLE_REQUESTS: "singleRequests",
    DELETED_REQUESTS: "deletedRequests",
};

export const ERROR_PAGE_TYPES = {
    PAGE_NOT_FOUND: "pageNotFound",
    BAD_REQUEST: HttpStatusCode.BadRequest.toString(),
    INTERNAL_SERVER_ERROR: HttpStatusCode.InternalServerError.toString(),
    CONFIG_ERROR: "configError",
};

// 1.1GB in bytes
export const ZIP_FILE_SIZE = 1.1 * 1024 * 1024 * 1024;

export const AVA_APP_NAME = "exemptions";

export const SessionRefreshState = {
    Idle: "idle",
    Refreshing: "refreshing",
} as const;

export type SessionRefreshStateType =
    (typeof SessionRefreshState)[keyof typeof SessionRefreshState];

export const FILE_NAME_DOES_NOT_EXIST = "document_file_does_not_exist";

// Default filter states for customer and certificate search
export const CUSTOMER_FILTER_DEFAULTS = {
    isCustomerDetails: true,
    isCertificateDetails: false,
    customerRegions: [],
    isMissingEmailAddress: false,
    isMissingcertificates: false,
    missingExposureZones: [],
    isBillToRelation: null,
    isShipToRelation: null,
    customerRelationShip: [],
    isDuplicateRelation: false,
    customerCodes: [],
    customerCodeError: "",
    customerCreatedDateAfter: "",
    customerCreatedDateBefore: "",
    customerModifiedDateAfter: "",
    customerModifiedDateBefore: "",
    certificateStatus: [],
    certificateRegions: [],
    exemptReasons: [],
    invalidReasons: [],
    isPurchaseOrder: false,
    invoiceNumber: "",
    certificateIds: "",
    certificateIdError: "",
    isCertificateImage: 0,
    certificateCreatedDateAfter: "",
    certificateCreatedDateBefore: "",
    certificateModifiedDateAfter: "",
    certificateModifiedDateBefore: "",
    certificateEffectiveDateAfter: "",
    certificateEffectiveDateBefore: "",
    certificateExpirationDateAfter: "",
    certificateExpirationDateBefore: "",
    certificateValidatedDateAfter: "",
    certificateValidatedDateBefore: "",
    isVendorFilter: false,
    savedSearch: "",
    customerCustomFields: {},
    certificateCustomFields: {},
    alternateId: "",
    customersLabels: [],
    excludeCustomerLabels: [],
    certificatesLabels: [],
    excludeCertificateLabels: [],
    nameOrCode: "",
    companyGroup: {},
    city: "",
    zip: "",
    address: "",
    phone: "",
    federalTaxID: "",
    email: "",
    customerExposures: [],
    localJurisdictions: [],
    isExemptionOverrideDetails: false,
    overrideExemptReasons: [],
    overrideRegions: [],
    overrideCreatedDateAfter: "",
    overrideCreatedDateBefore: "",
    overrideEffectiveDateAfter: "",
    overrideEffectiveDateBefore: "",
    overrideExpirationDateAfter: "",
    overrideExpirationDateBefore: "",
    overrideLicenseNumber: "",
    isOverrideActive: 0,
};

export const CERTIFICATE_FILTER_DEFAULTS = {
    isCustomerDetails: false,
    isCertificateDetails: true,
    customerRegions: [],
    isMissingEmailAddress: false,
    isBillToRelation: null,
    isShipToRelation: null,
    customerRelationShip: [],
    isDuplicateRelation: false,
    customerCodes: [],
    customerCodeError: "",
    isCustomerDateFilter: false,
    customerCreatedDateAfter: "",
    customerCreatedDateBefore: "",
    customerModifiedDateAfter: "",
    customerModifiedDateBefore: "",
    certificatesLabels: [],
    excludeCertificateLabels: [],
    customersLabels: [],
    excludeCustomerLabels: [],
    certificateStatus: [],
    certificateRegions: [],
    exemptReasons: [],
    invalidReasons: [],
    isPurchaseOrder: false,
    invoiceNumber: "",
    certificateIds: "",
    certificateIdError: "",
    isCertificateImage: 0,
    isCertificateActive: false,
    isCertificateDateFilter: false,
    certificateCreatedDateAfter: "",
    certificateCreatedDateBefore: "",
    certificateModifiedDateAfter: "",
    certificateModifiedDateBefore: "",
    certificateEffectiveDateAfter: "",
    certificateEffectiveDateBefore: "",
    certificateExpirationDateAfter: "",
    certificateExpirationDateBefore: "",
    certificateValidatedDateAfter: "",
    certificateValidatedDateBefore: "",
    savedSearch: "",
    certificateHasCustomers: 0,
    isCertActive: 0,
    certificateCustomFields: {},
    customerCustomFields: {},
    nameOrCode: "",
    reviewedBy: [],
    companyGroup: {},
    customerExposures: [],
    productExemptionCategories: [],
    localJurisdictions: [],
};

export const SameAsLimit = {
    Premium: 10000,
    Default: 10,
};

export const EXPIRED_CERT_AVS_BANNER = "The expiration date on the certificate is in the past.";

/**
 * Supported locales for the ECM locale switcher in the global header.
 * Source of truth lives in src/i18n/i18n.config.ts.
 */
export { SUPPORTED_LOCALES } from "../i18n/i18n.config";
